/**
 * Multi-threshold document-expiry reminders. Admins configure how many days
 * before a document expires the vendor is reminded (e.g. [30, 20, 10] gives
 * three escalating reminders). `submission_files.last_reminder_threshold`
 * stores the smallest threshold already notified, which keeps the daily cron
 * idempotent without per-send bookkeeping rows.
 */

/** Behaviour when the setting has never been configured: the historical fixed 14-day rule. */
export const DEFAULT_DOCUMENT_REMINDER_DAYS: readonly number[] = [14];

export const MAX_REMINDER_DAYS_ENTRIES = 10;

/**
 * Cleans a raw day list: integers within 1–365, deduped, sorted descending,
 * capped at MAX_REMINDER_DAYS_ENTRIES. Descending order means index 0 is the
 * widest reminder window.
 */
export function normalizeReminderDays(days: number[]): number[] {
  const cleaned = days.filter((d) => Number.isInteger(d) && d >= 1 && d <= 365);
  return [...new Set(cleaned)].sort((a, b) => b - a).slice(0, MAX_REMINDER_DAYS_ENTRIES);
}

/**
 * The threshold to notify for a file, or null when nothing is due.
 * Rule: take the smallest threshold T with daysLeft <= T (the latest crossing);
 * send only if we have not already notified at T or a smaller threshold. After
 * missed cron runs this sends exactly one catch-up email, never a backlog.
 */
export function pickReminderThreshold(
  daysLeft: number,
  thresholds: readonly number[],
  lastSent: number | null,
): number | null {
  let target: number | null = null;
  for (const t of thresholds) {
    if (daysLeft <= t && (target === null || t < target)) target = t;
  }
  if (target === null) return null;
  if (lastSent !== null && lastSent <= target) return null;
  return target;
}
